import React, {useRef, useState, useEffect} from 'react'
import styled from 'styled-components'
import {Box, Link, Button, ArrowDown, useWindowSize} from '@miro-site/core'
import {theme as defaultTheme} from '@miro-site/design-tokens'

type Category = {
  title: string
  path?: string
  groups?: Groups[]
}

type Groups = {
  title?: string
  items: Item[]
  viewAllLink?: Item | null
}

type Item = {
  title: string
  path: string
  description?: string | null
}

interface AccordionMenuProps {
  categories: Category[]
  className?: string
}

const AccordionContainer = styled(Box).attrs(({theme}) => ({
  as: 'nav',
  $styles: {
    maxWidth: theme.header ? theme.header.container.maxWidth : null,
  },
  $pl: theme.accordion.gutter,
  $pr: theme.accordion.gutter,
  $mx: 'auto',
}))``

const AccordionItem = styled(Box).attrs({
  as: 'li',
})`
  ${props => props.theme.normalizeStyles(props.theme.accordion.itemStyles)}
`

const AccordionCategory = styled(Box).attrs<{$isActive: boolean}>(({$isActive, theme}) => ({
  $styles: {
    display: ['block', 'inline-block'],
    fontSize: theme.accordion.category.fontSize,
    fontWeight: theme.accordion.category.fontWeight,
    lineHeight: theme.accordion.category.lineHeight,
    color: $isActive ? defaultTheme.colors.outspoken : defaultTheme.colors.primaryWhite,
    borderBottom: '1px solid transparent',
    transition: '250ms ease',
    transitionProperty: 'color, border-color',
  },
}))<{$isActive?: boolean}>``

const AccordionCategoryWrapper = styled(Box).attrs(({theme}) => ({
  $styles: {
    position: 'relative',
    cursor: 'pointer',
  },
  $py: theme.accordion.categoryWrapper.padding,
}))`
  @media (hover: hover) {
    &:hover ${AccordionCategory} {
      ${props =>
        props.theme.normalizeStyles({
          color: defaultTheme.colors.outspoken,
          borderColor: defaultTheme.colors.outspoken,
        })}
    }
  }
`

const AccordionButton = styled(Button).attrs(props => ({
  $styles: {
    display: 'block',
    ':focus': {
      ...props.theme.focus.outlines.withOffset,
    },
  },
}))``

const AccordionLink = styled(Link).attrs(props => ({
  $styles: {
    display: ['block', 'inline-block'],
    ':focus': {
      ...props.theme.focus.outlines.withOffset,
    },
  },
}))``

const AccordionInnerLink = styled(Link).attrs(props => ({
  $styles: {
    fontSize: props.theme.accordion.innerLink.fontSize,
    lineHeight: props.theme.accordion.innerLink.lineHeight,
    position: 'relative',
    ':after': {
      content: `''`,
      background: '#211D45',
      position: 'absolute',
      top: -12,
      left: -15,
      right: -45,
      bottom: -12,
      borderRadius: 8,
      opacity: 0,
      transition: 'opacity 250ms ease',
    },
    ':focus': {
      ...props.theme.focus.outlines.withOffset,
    },
    '@media (hover: hover)': {
      ':hover:after': {
        opacity: 1,
      },
    },
  },
}))``

const AccordionSubcategoryContainer = styled(Box).attrs<{$isActive: boolean}>(({$isActive}) => ({
  $styles: {
    visibility: $isActive ? 'visible' : 'hidden',
    overflow: 'hidden',
    transition: '400ms ease',
    transitionProperty: 'height, visibility',
  },
}))<{$isActive: boolean}>``

const AccordionSubcategoryWrapper = styled(Box).attrs(({theme}) => ({
  $pl: theme.accordion.subcategoryWrapper.padding.left,
  $pb: theme.accordion.subcategoryWrapper.padding.bottom,
}))``

const AccordionSubcategory = styled(Box).attrs(({theme}) => ({
  as: 'h4',
  $styles: {
    fontSize: theme.accordion.subcategory.fontSize,
    lineHeight: theme.accordion.subcategory.lineHeight,
    fontWeight: theme.accordion.subcategory.fontWeight,
    color: theme.accordion.subcategory.color,
  },
}))``

const AccordionInnerList = styled(Box).attrs<{$isOffset: boolean}>(({theme, $isOffset}) => ({
  as: 'ul',
  $mt: $isOffset ? theme.accordion.innerList.margin.top : null,
}))<{$isOffset: boolean}>``

const AccordionInnerItem = styled(Box).attrs(({theme}) => ({
  as: 'li',
  $mt: theme.accordion.innerItem.margin.top,
}))`
  &:first-child {
    margin-top: 0;
  }
`

const AccordionLinkDescription = styled(Box).attrs(({theme}) => ({
  $styles: {
    fontSize: theme.accordion.description.fontSize,
    lineHeight: theme.accordion.description.lineHeight,
    color: theme.accordion.description.color,
    opacity: theme.accordion.description.opacity,
  },
  $mt: theme.accordion.description.margin.top,
}))``

const AccordionAdditionalLink = styled(Link).attrs(({theme}) => ({
  $mt: theme.accordion.additionalLink.margin.top,
}))``

const AccordionIcon = styled(Box).attrs<{$isActive?: boolean}>(({$isActive}) => ({
  $styles: {
    position: 'absolute',
    top: '50%',
    right: 0,
    transform: $isActive ? 'translateY(-50%) scaleY(-1)' : 'translateY(-50%) scaleY(1)',
    transition: 'transform 250ms ease',
  },
}))<{$isActive: boolean}>``

const AccordionMenu = (props: AccordionMenuProps): React.ReactElement => {
  const {categories} = props

  const [expandedItems, setExpandedItems] = useState<boolean[]>(
    categories.reduce((acc: boolean[], c, i) => {
      acc[i] = false

      return acc
    }, []),
  )

  const {width, height} = useWindowSize()

  const linkRefs = useRef<Array<HTMLAnchorElement | null>>([])

  const containerRefs = useRef<Array<HTMLElement | null>>([])

  useEffect(() => {
    containerRefs.current.forEach((container, i) => {
      if (!container) {
        return
      }

      container.style.height = expandedItems[i]
        ? `${(container.firstChild as HTMLElement)?.offsetHeight || 0}px`
        : `0px`
    })
  }, [expandedItems, height, width])

  return (
    <AccordionContainer aria-label="Accordion menu" className={props.className}>
      <ul>
        {categories.map((category, categoryIndex) => {
          return (
            <AccordionItem key={categoryIndex}>
              {category.path ? (
                <AccordionCategoryWrapper
                  onClick={() => {
                    const link = linkRefs.current[categoryIndex]

                    if (link !== null) {
                      link.click()
                    }
                  }}
                >
                  <AccordionLink
                    ref={ref => {
                      linkRefs.current[categoryIndex] = ref
                    }}
                    href={category.path}
                  >
                    <AccordionCategory>{category.title}</AccordionCategory>
                  </AccordionLink>
                </AccordionCategoryWrapper>
              ) : (
                <AccordionCategoryWrapper
                  onClick={() =>
                    setExpandedItems({
                      ...expandedItems,
                      [categoryIndex]: !expandedItems[categoryIndex],
                    })
                  }
                >
                  <AccordionButton aria-expanded={expandedItems[categoryIndex]}>
                    <AccordionCategory $isActive={expandedItems[categoryIndex]}>
                      {category.title}
                    </AccordionCategory>
                  </AccordionButton>

                  <AccordionIcon $isActive={expandedItems[categoryIndex]} aria-hidden={true}>
                    <ArrowDown />
                  </AccordionIcon>
                </AccordionCategoryWrapper>
              )}

              {category.groups && (
                <AccordionSubcategoryContainer
                  ref={ref => {
                    containerRefs.current[categoryIndex] = ref
                  }}
                  $isActive={expandedItems[categoryIndex]}
                  aria-hidden={!expandedItems[categoryIndex]}
                >
                  <div>
                    {category.groups.map((subcategory, subcategoryIndex) => {
                      return (
                        <AccordionSubcategoryWrapper key={subcategoryIndex}>
                          {subcategory.title && (
                            <AccordionSubcategory>{subcategory.title}</AccordionSubcategory>
                          )}

                          <AccordionInnerList $isOffset={!!subcategory.title}>
                            {subcategory.items.map((link, linkIndex) => {
                              return (
                                <AccordionInnerItem key={linkIndex}>
                                  <AccordionInnerLink
                                    href={link.path}
                                    $color="white"
                                    $size="medium"
                                  >
                                    <Box $styles={{position: 'relative', zIndex: 2}}>
                                      {link.title}

                                      {link.description && (
                                        <AccordionLinkDescription>
                                          {link.description}
                                        </AccordionLinkDescription>
                                      )}
                                    </Box>
                                  </AccordionInnerLink>
                                </AccordionInnerItem>
                              )
                            })}
                          </AccordionInnerList>

                          {subcategory.viewAllLink && (
                            <AccordionAdditionalLink
                              $variant="regular"
                              $color="white"
                              $size="medium"
                              withArrow={true}
                              href={subcategory.viewAllLink.path}
                            >
                              {subcategory.viewAllLink.title}
                            </AccordionAdditionalLink>
                          )}
                        </AccordionSubcategoryWrapper>
                      )
                    })}
                  </div>
                </AccordionSubcategoryContainer>
              )}
            </AccordionItem>
          )
        })}
      </ul>
    </AccordionContainer>
  )
}

export default AccordionMenu
